import { SearchableSelector, type SearchableOption } from "@/components/searchable-selector";
import type { AnalyticsFeeder, GrafanaReportFilters } from "@/lib/api";
import { formatDeviceId } from "@/lib/format";
import {
  ALL_REPORT_VALUES,
  CATALOGUE_FILTERS,
  catalogueOptions,
  catalogueValues,
  type ActiveGrafanaReport,
} from "@/lib/report-filters";

/** The same catalog and filter semantics serve dashboards and report delivery. */
export function ReportScopeFilters({
  report, filters, feeders, onChange, layout = "sidebar",
}: {
  report: ActiveGrafanaReport;
  filters: GrafanaReportFilters;
  feeders: readonly AnalyticsFeeder[];
  onChange: (filters: GrafanaReportFilters) => void;
  layout?: "sidebar" | "form";
}): JSX.Element {
  const primary = new Set<string>(layout === "form"
    ? ["program", "disco", "substation", "band", "feeder"]
    : ["program", "disco", "controller", "substation", "feeder"]);
  const advanced = CATALOGUE_FILTERS.filter(({ key }) => !primary.has(key));
  const applied = advanced.filter(({ key }) => filters[key] !== ALL_REPORT_VALUES).length;
  const renderControl = (definition: (typeof CATALOGUE_FILTERS)[number]): JSX.Element => {
    const options = catalogueOptions(feeders, filters, definition.key);
    const values = Array.from(new Set(options.flatMap((feeder) => catalogueValues(feeder, definition))
      .filter((value) => typeof value === "string" && value.length > 0)))
      .sort((a, b) => a.localeCompare(b));
    const allowsAll = definition.key !== "program" && !(definition.key === "feeder" && report === "performance");
    const selected = filters[definition.key];
    const choices: SearchableOption[] = [];
    if (allowsAll) {
      const plural = definition.label === "NERC Class" ? "classes"
        : definition.label === "DisCo" ? "DisCos" : `${definition.label.toLowerCase()}s`;
      choices.push({ value: ALL_REPORT_VALUES, label: `All ${plural}` });
    }
    if (selected && selected !== ALL_REPORT_VALUES && !values.includes(selected))
      choices.push({ value: selected, label: `${selected} (Unavailable)`, disabled: true });
    for (const value of values) {
      const option = options.find((feeder) => catalogueValues(feeder, definition).includes(value));
      choices.push({
        value,
        label: definition.key === "program" ? (option?.program_name ?? value)
          : definition.key === "controller" ? (option?.controller_name ?? value)
          : definition.key === "feeder" ? (option?.feeder_name ?? value) : value || "Unspecified",
        context: definition.key === "controller" ? formatDeviceId(value)
          : definition.key === "feeder" ? `${option?.substation ?? ""} · meter ${option?.slot ?? ""}` : undefined,
        searchText: definition.key === "feeder" && option
          ? Object.values(option).filter((field) => typeof field === "string").join(" ") : value,
      });
    }
    return (
      <label key={definition.key} className="block min-w-0 space-y-1 text-xs font-medium text-muted-foreground">
        <span>{definition.label}</span>
        <SearchableSelector label={definition.label} value={selected ?? ALL_REPORT_VALUES}
          options={choices} size={layout === "form" ? "form" : "filter"}
          onChange={(value) => {
            const next = { ...filters, [definition.key]: value };
            const index = CATALOGUE_FILTERS.findIndex(({ key }) => key === definition.key);
            for (const { key } of CATALOGUE_FILTERS.slice(index + 1)) next[key] = ALL_REPORT_VALUES;
            onChange(next);
          }} />
      </label>
    );
  };
  return (
    <div className={layout === "form" ? "grid min-w-0 gap-3 sm:grid-cols-2" : "grid min-w-0 gap-2"}>
      {CATALOGUE_FILTERS.filter(({ key }) => primary.has(key)).map(renderControl)}
      <details className="min-w-0 rounded-md border px-3 py-2">
        <summary className="cursor-pointer text-xs font-medium text-muted-foreground">
          More filters{applied ? ` (${applied} applied)` : ""}
        </summary>
        <div className="mt-3 grid min-w-0 gap-3">{advanced.map(renderControl)}</div>
      </details>
    </div>
  );
}
